// hooks/useVideoControls.ts
import { useCallback } from "react";
import { SLIDER_PRECISION, VideoPlayerState } from "./useVideoPlayerState";

interface UseVideoControlsProps {
  playerState: VideoPlayerState;
}

export const useVideoControls = ({
  playerState
}: UseVideoControlsProps) => {
  const {
    duration,
    currentTime,
    isPlaying,
    setIsPlaying,
    setCurrentTime,
    setSliderValue,
    getVideos,
    getPrimaryVideo,
  } = playerState;

  // Handler for video's timeupdate event
  const handleVideoTimeUpdate = useCallback(() => {
    const primary = getPrimaryVideo();
    if (!primary || duration === 0) return;

    const time = primary.currentTime;
    setCurrentTime(time);
    setSliderValue((time / duration) * SLIDER_PRECISION);
  }, [duration, getPrimaryVideo, setCurrentTime, setSliderValue, isPlaying]);

  // Handler for play/pause button (CORRECTED)
  const togglePlay = useCallback(() => {
    setIsPlaying(prevIsPlaying => !prevIsPlaying);
  }, [setIsPlaying]);

  const handleSliderDrag = useCallback((event: unknown, newValue: number | number[]) => {
    console.log("Drag", " timestamp = ", Date.now())
    if (typeof newValue !== 'number' || duration === 0) return;

    const newTime = (newValue / SLIDER_PRECISION) * duration;
    getVideos().forEach(v => {
      if (v) {
        v.currentTime = newTime;
      }
    });

    setSliderValue(newValue);
    console.log("drag done newTime = ", newTime, " timestamp = ", Date.now())

  }, [duration, getVideos, setSliderValue]);


  // RENAMED: This handler now fires only when the user releases the slider.
  const handleSliderChangeCommitted = useCallback((event: unknown, newValue: number | number[]) => {
    console.log("committed", " timestamp = ", Date.now())
    if (typeof newValue !== 'number' || duration === 0) return;

    // Pause the video if it was playing.
    if (isPlaying) {
      // It's better to let the state change propagate down.
      setIsPlaying(false);
    }

    // Calculate the final time.
    const newTime = (newValue / SLIDER_PRECISION) * duration;

    // Do the "official" React state update to sync everything.
    setSliderValue(newValue);
    setCurrentTime(newTime);

    console.log("commit done time =", newTime, " timestamp = ", Date.now())

  }, [duration, isPlaying, setIsPlaying, setSliderValue, setCurrentTime]);

  // Handler for capturing a frame
  const captureFrame = useCallback(() => {
    const primary = getPrimaryVideo();
    if (!primary) return;
    const canvas = document.createElement("canvas");
    canvas.width = primary.videoWidth;
    canvas.height = primary.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(primary, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `frame-${Math.floor(currentTime * 1000)}ms.png`;
    link.click();
  }, [currentTime, getPrimaryVideo]);

  return {
    handleVideoTimeUpdate,
    togglePlay,
    handleSliderDrag,
    handleSliderChangeCommitted,
    captureFrame,
  };
};